import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Gift, Crown, Plus } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isMember, becomeMember } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradingMembership, setUpgradingMembership] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBookings();
    fetchRentals();
  }, [isAuthenticated]);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRentals = async () => {
    try {
      const response = await fetch(`${API}/rentals`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRentals(data);
      }
    } catch (error) {
      console.error('Error fetching rentals:', error);
    }
  };

  const handleBecomeMember = async () => {
    setUpgradingMembership(true);
    try {
      await becomeMember();
      toast.success('Welcome to the club! You now have 20% off your first shoot!');
    } catch (error) {
      toast.error('Failed to upgrade membership');
    } finally {
      setUpgradingMembership(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-secondary';
      case 'pending':
        return 'text-yellow-500';
      case 'cancelled':
        return 'text-destructive';
      default:
        return 'text-muted-foreground';
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="member-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-4xl font-bold mb-2">
                  Welcome, <span className="gradient-text">{user?.name}</span>
                </h1>
                <p className="text-muted-foreground">Manage your bookings and membership</p>
              </div>
              {isMember && (
                <div className="member-badge flex items-center px-4 py-2">
                  <Crown className="w-4 h-4 mr-2" />
                  Member
                </div>
              )}
            </div>
          </div>

          {/* Member Benefits Card (for non-members) */}
          {!isMember && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="mb-8 bg-gradient-to-r from-primary/20 to-secondary/20 border-primary/30">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center mb-2">
                        <Gift className="w-6 h-6 text-primary mr-2" />
                        <h3 className="text-xl font-semibold">Become a Member</h3>
                      </div>
                      <ul className="text-muted-foreground space-y-1">
                        <li>✓ 20% off your first photoshoot</li>
                        <li>✓ Add guests and split costs evenly</li>
                        <li>✓ Priority booking access</li>
                      </ul>
                    </div>
                    <Button
                      onClick={handleBecomeMember}
                      disabled={upgradingMembership}
                      className="btn-primary whitespace-nowrap"
                      data-testid="become-member-btn"
                    >
                      {upgradingMembership ? 'Upgrading...' : 'Become a Member'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Member Status Card (for members) */}
          {isMember && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="mb-8 bg-card/50 border-primary/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold flex items-center">
                        <Crown className="w-5 h-5 text-primary mr-2" />
                        Member Benefits Active
                      </h3>
                      <p className="text-muted-foreground mt-1">
                        {user?.first_shoot_used
                          ? 'First shoot discount used'
                          : '20% discount available on your first shoot!'}
                      </p>
                    </div>
                    {!user?.first_shoot_used && (
                      <div className="discount-badge">20% OFF READY</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Bookings */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Your Bookings</h2>
            <Button
              onClick={() => navigate('/booking')}
              className="btn-primary"
              data-testid="new-booking-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 rounded-xl image-loading" />
              ))}
            </div>
          ) : bookings.length === 0 ? (
            <Card className="bg-card/50 border-white/10">
              <CardContent className="p-12 text-center">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Bookings Yet</h3>
                <p className="text-muted-foreground mb-6">
                  You haven't booked any photoshoots yet.
                </p>
                <Button onClick={() => navigate('/booking')} className="btn-primary">
                  Book Your First Shoot
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <motion.div
                  key={booking.booking_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className="bg-card/50 border-white/10 hover:border-primary/30 transition-colors"
                    data-testid={`booking-card-${booking.booking_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{booking.primary_snake_name}</h3>
                            <span className={`text-sm font-medium capitalize ${getStatusColor(booking.booking_status)}`}>
                              {booking.booking_status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {booking.date}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {booking.start_time} ({booking.duration_hours}hr)
                            </span>
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              <span className="capitalize">{booking.location_type.replace('_', ' ')}</span>
                            </span>
                          </div>
                          {booking.discount_applied > 0 && (
                            <div className="flex items-center text-sm text-secondary">
                              <Gift className="w-4 h-4 mr-1" />
                              Member discount: -${booking.discount_applied.toFixed(2)}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">
                            ${booking.total_price.toFixed(2)}
                          </p>
                          <p className={`text-sm ${
                            booking.payment_status === 'paid' ? 'text-secondary' : 'text-yellow-500'
                          }`}>
                            {booking.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}

          {/* Rentals Section */}
          <div className="flex items-center justify-between mb-4 mt-12">
            <h2 className="text-2xl font-semibold">Your Rentals</h2>
            <Button
              onClick={() => navigate('/rentals')}
              className="btn-secondary"
              data-testid="new-rental-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Rental
            </Button>
          </div>

          {rentals.length === 0 ? (
            <Card className="bg-card/50 border-white/10">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  No rentals yet. Rent snakes for music videos, educational events, or other projects!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {rentals.map((rental, index) => (
                <motion.div
                  key={rental.rental_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card
                    className="bg-card/50 border-white/10 hover:border-primary/30 transition-colors"
                    data-testid={`rental-card-${rental.rental_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{rental.primary_snake_name}</h3>
                            <span className="text-xs bg-muted px-2 py-1 rounded capitalize">
                              {rental.rental_type.replace('_', ' ')}
                            </span>
                            <span className={`text-sm font-medium capitalize ${getStatusColor(rental.rental_status)}`}>
                              {rental.rental_status}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {rental.date}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {rental.start_time} ({rental.duration_hours}hr)
                            </span>
                            {rental.location && (
                              <span className="flex items-center">
                                <MapPin className="w-4 h-4 mr-1" />
                                {rental.location}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">
                            ${rental.total_price.toFixed(2)}
                          </p>
                          <p className={`text-sm ${
                            rental.payment_status === 'paid' ? 'text-secondary' : 'text-yellow-500'
                          }`}>
                            {rental.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard;
